import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { columnDefs, defaultColDef } from './definitions-config';

import { getAllUserDetails } from '../../../../services/apiService';

import { toast } from 'react-toastify';
import { changeCohort } from './services/api';
import useUrlFilters from '../../../../extras/useUrlFilters';

const pageSize = 10;

const userType = 'paid';

const CohortContext = React.createContext();

function PaidUsersTable({
  isApplyDefaultFilter,
  setIsApplyDefaultFilter,
  setMetricRefreshCount,
}) {
  const [gridApi, setGridApi] = useState(null);
  const gridRef = useRef();
  const { gridFilterModel, setGridFilterModel } = useUrlFilters({
    enableGridFilter: true,
  });

  const getServerSideDatasource = () => {
    return {
      getRows: (params) => {
        const getDateFilterObj = (dateFilter) => {
          if (!dateFilter) return null;
          const date = dateFilter.dateFrom.split(' ')[0];
          let sign = '';
          if (dateFilter.type === 'equals') sign = '$eq';
          else if (dateFilter.type === 'greaterThan') sign = '$gt';

          return {
            [sign]: date,
          };
        };

        const getProgressFilterObj = (progressFilter) => {
          if (!progressFilter) return null;

          const num = progressFilter.filter;
          let sign = '';
          if (progressFilter.type === 'equals') sign = '$eq';
          else if (progressFilter.type === 'greaterThan') sign = '$gt';
          else if (progressFilter.type === 'lessThan') sign = '$lt';
          else if (progressFilter.type === 'notEqual') sign = '$ne';

          return {
            [sign]: num,
          };
        };

        const body = {
          filter: {
            firstName: params.request?.filterModel?.name?.filter || null,
            email: params.request?.filterModel?.email?.filter || null,
            phone: params.request?.filterModel?.phone?.filter || null,
            cohort: params.request?.filterModel?.cohort?.filter || null,
            planType: params.request?.filterModel?.planType?.values || null,
            courseType: params.request?.filterModel?.courseType?.values || null,
            sspStatus:
              params.request?.filterModel?.sspStatus?.values &&
              params.request?.filterModel?.sspStatus?.values.length > 0
                ? params.request?.filterModel?.sspStatus?.values
                : null,

            almaXCohort:
              params.request?.filterModel?.almaXCohort?.values &&
              params.request?.filterModel?.almaXCohort?.values.length > 0
                ? params.request?.filterModel?.almaXCohort?.values
                : null,

            courseProgress: getProgressFilterObj(
              params.request?.filterModel?.courseProgress
            ),
            signupDate: getDateFilterObj(
              params.request?.filterModel?.signupDate
            ),
            enrollmentDate: getDateFilterObj(
              params.request?.filterModel?.enrollmentDate
            ),

            userType,
          },

          pagination: {
            start: params.request.startRow,
            end: params.request.endRow,
          },
        };

        try {
          getAllUserDetails(body).then((response) => {
            params.successCallback(
              response?.data || [],
              response?.pagination?.totalCount || 0
            );
          });
        } catch (error) {
          console.error(error);
          params.failCallback();
          toast.error('Failed to fetch data');
        }
      },
    };
  };

  const cohortParams = {
    cohortChangeFn: async (courseType, userId, cohortId, reasonOfChange) => {
      const body = {
        userId,
        cohortId,
        courseType,
        reasonOfChange,
      };

      try {
        await changeCohort(body);
        reloadData();
        // setMetricRefreshCount((count) => count + 1);
        toast.success(
          'Cohort changed successfully. Changes will be reflected after 5 minutes.'
        );
      } catch (error) {
        console.error(error);
        toast.error('Error: ' + error?.response?.data?.message);
      }
    },
  };

  const onGridReady = React.useCallback(
    (params, mIsApplyDefaultFilter = true) => {
      setGridApi(params);

      if (mIsApplyDefaultFilter) {
        // Restore filter model from URL if available, otherwise clear
        if (gridFilterModel && Object.keys(gridFilterModel).length > 0) {
          params.api.setFilterModel(gridFilterModel);
        } else {
          params.api.setFilterModel({});
        }
        setIsApplyDefaultFilter(false);
      }

      const datasource = getServerSideDatasource();
      params.api.setServerSideDatasource(datasource);
    },
    [gridFilterModel]
  );

  const onFilterChanged = (params) => {
    const filterModel = params.api.getFilterModel();
    setGridFilterModel(filterModel);
  };

  const reloadData = () => {
    if (gridApi) {
      onGridReady(gridApi, isApplyDefaultFilter);
    }
  };

  useEffect(() => {
    if (isApplyDefaultFilter) reloadData();
  }, [isApplyDefaultFilter]);

  return (
    <div>
      <div
        style={{
          backgroundColor: 'var(--card-background, #fff)',
          borderRadius: 16,
          border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
          boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
          overflow: 'hidden',
        }}
      >
        <div className='ag-theme-alpine' style={{ width: '100%' }}>
          <CohortContext.Provider value={cohortParams}>
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              rowHeight={60}
              domLayout='autoHeight'
              rowModelType='serverSide'
              serverSideStoreType='partial'
              animateRows={true}
              serverSideInfiniteScroll={true}
              pagination={true}
              suppressPaginationPanel={false}
              paginationPageSize={pageSize}
              cacheBlockSize={pageSize}
            />
          </CohortContext.Provider>
        </div>
      </div>
    </div>
  );
}

export default PaidUsersTable;
export { CohortContext };
